// client/src/components/ui/Glyph.tsx — an icon in a tinted square · the console's visual pass
// (D-1835, lifted out of `states.tsx` by the third round, D-1842).
//
// The one shape every head-of-surface icon in the console takes: the empty state's, the
// forbidden state's, a dialog's, a settings card's. A bare 48 px grey outline floating in white
// space was the first thing the audit listed; a square with a 10 % tint behind the icon is what
// makes an icon read as placed rather than dropped.
//
// `tone` is the one thing a caller decides. `brand` is an invitation — an empty list, a form —
// and the four semantic tones follow §4.8's map; `neutral` is a refusal, a failure, a fact. The
// square is `aria-hidden` always: it carries no meaning the title beside it does not.

import { Icon, type IconName } from '@/components/icons';

export type GlyphTone = 'brand' | 'neutral' | 'success' | 'warning' | 'danger' | 'info';
export type GlyphSize = 'sm' | 'md' | 'lg';

const TONE_CLASS: Record<GlyphTone, string> = {
  brand: 'bg-primary/10 text-primary',
  neutral: 'bg-base-200 text-gray-500',
  success: 'bg-success/12 text-green-700',
  warning: 'bg-warning/15 text-amber-700',
  danger: 'bg-error/10 text-red-700',
  info: 'bg-info/10 text-blue-700',
};

const SIZE_CLASS: Record<GlyphSize, { box: string; icon: string }> = {
  sm: { box: 'h-9 w-9 rounded-lg', icon: 'h-4.5 w-4.5' },
  md: { box: 'h-11 w-11 rounded-xl', icon: 'h-5 w-5' },
  lg: { box: 'h-14 w-14 rounded-2xl', icon: 'h-7 w-7' },
};

export function Glyph({
  icon,
  tone = 'brand',
  size = 'md',
  className = '',
}: {
  icon: IconName;
  tone?: GlyphTone;
  size?: GlyphSize;
  className?: string;
}) {
  const sizes = SIZE_CLASS[size];
  return (
    <span
      className={`grid shrink-0 place-items-center ${sizes.box} ${TONE_CLASS[tone]} ${className}`}
      aria-hidden="true"
    >
      <Icon name={icon} className={sizes.icon} />
    </span>
  );
}
